import React from 'react';
import Skeleton from 'react-loading-skeleton';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const MONTH_COLORS = [
  '#4f9dee', '#25b39c', '#f79f2f', '#e95f77',
  '#6c67de', '#1fb8de', '#8bb046', '#f28456',
  '#7f8ce6', '#d29d35', '#c474f0', '#5fbf79'
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div style={{
      background: 'rgba(10, 18, 36, 0.97)',
      border: '1px solid rgba(160, 186, 228, 0.55)',
      borderRadius: 10,
      padding: '10px 12px',
      color: '#f5f9ff',
      fontSize: 13,
      boxShadow: '0 8px 22px rgba(2, 9, 22, 0.45)'
    }}>
      <div style={{ fontWeight: 800, marginBottom: 5, color: '#ffffff' }}>Day {label}</div>
      {payload.map(item => (
        <div key={item.dataKey} style={{ color: item.color, marginTop: 3, fontWeight: 700 }}>
          {item.name}: {item.value}
        </div>
      ))}
    </div>
  );
};

export default class AppointmentLineChart extends React.Component {
  constructor(props) {
    super(props);
    this.state = { chartData: [], monthNames: [], loading: true };
  }

  componentDidMount() {
    this.fetchData();
  }

  // PE-438: the statistics page appends its filter query; without the prop the
  // dashboard keeps fetching the bare endpoint exactly as before.
  componentDidUpdate(prevProps) {
    if (prevProps.query !== this.props.query) this.fetchData();
  }

  componentWillUnmount() {
    this.unmounted = true;
  }

  fetchData() {
    // Rapid filter changes can resolve out of order; only the latest request
    // may write state. Inert on the dashboard, where query never changes.
    const requestId = (this.requestId = (this.requestId || 0) + 1);
    this.setState({ loading: true });
    fetch('/api/appointments/line_chart' + (this.props.query || ''))
      .then(res => res.ok ? res.json() : Promise.reject(res))
      .then(data => {
        if (this.unmounted || requestId !== this.requestId) return;
        const monthNames = data.map(series => series.name);

        // chart_json returns data as array of [day, count] tuples
        // Convert each series.data into a lookup map
        const seriesMaps = data.map(series => {
          const map = {};
          series.data.forEach(([day, count]) => {
            map[day] = count;
          });
          return { name: series.name, map };
        });

        // Collect all unique days across all months
        const daySet = new Set();
        seriesMaps.forEach(s => {
          Object.keys(s.map).forEach(day => daySet.add(day));
        });
        const days = Array.from(daySet).sort();

        // Pivot: each row = { day: "01", January: 5, February: 3, ... }
        const chartData = days.map(day => {
          const row = { day };
          seriesMaps.forEach(s => {
            row[s.name] = s.map[day] || 0;
          });
          return row;
        });

        this.setState({ chartData, monthNames, loading: false });
      })
      .catch(() => {
        if (this.unmounted || requestId !== this.requestId) return;
        this.setState({ loading: false });
      });
  }

  render() {
    const { chartData, monthNames, loading } = this.state;

    if (!loading && (!chartData || chartData.length === 0 || !monthNames.length)) {
      return <div className="text-center text-muted py-5">No data available</div>;
    }

    return (
      <div>
        {loading ? (
          <div style={{ height: 380, padding: '12px 0' }}>
            <Skeleton height={340} borderRadius={14} />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <LineChart data={chartData} margin={{ top: 16, right: 16, left: -10, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 5" vertical={false} stroke="#e7eef8" />
              <XAxis
                dataKey="day"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#7f8ea8', fontWeight: 600 }}
              />
              <YAxis
                allowDecimals={false}
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 12, fill: '#8a98b0' }}
                width={32}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ paddingTop: 8 }} />
              {monthNames.map((name, i) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={MONTH_COLORS[i % MONTH_COLORS.length]}
                  strokeWidth={2.2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    );
  }
}
